//============================================================================
// Name        : UndoProcessDialog.tsx
// Purpose     : Undo process dialog component
// Created     : 2023-06-06
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

// tslint:disable-next-line:no-unused-expression
/** @jsx jsx */ jsx;
import { jsx } from "@emotion/core";
import React from "react";
import { connect } from "react-redux";
import { IUndoProcessDialogProps } from "./types";

class UndoProcessDialog extends React.Component<IUndoProcessDialogProps> {
    getClassName = () => {
        return this.props.show ? "modal show" : "modal fade";
    }

    handleContinue = () => {
        location.reload();
    }

    getStatus = (status: string) => {
        switch (status) {
            case "loading":
                return (<span> - Checking</span>);
            case "success":
                return (<span className="text-success"> - Success</span>);
            case "failed":
                return (<span className="text-danger"> - Failed</span>);
            default:
                return null;
        }
    }

    showError = (error: any) => {
        switch (error.type) {
            case 'live_status':
                return this.showErrorLiveStatus(error.tags);
            case 'quantities':
                return this.showErrorQuantities(error.tags);
            case 'allocations':
                return this.showErrorAllocations(error.allocation_items, error.allocations);
            case 'nav':
                return this.showErrorNav(error.message);
        }
    }

    showErrorLiveStatus = (tags: Array<any>) => (
        <div>
            The reason you cannot UNDO the receiving is because following tag(s):
            <ul>{tags.map((tag: any) => (<li key={tag.asset_id}>#{tag.asset_id} is {tag.status}</li>))}</ul>
            <ul>
                <li>If you have QH tags you must remove the QH and then come back and retry to UNDO</li>
                <li>If you have Tag Killed / Sold / consumed Tags UNDO is not possible ever again via Senitron due to ERP rules, and Further modifications to PO must be managed through your ERP. Contact your back office or ERP support team.</li>
            </ul>
        </div>
    );

    showErrorQuantities = (tags: Array<any>) => (
        <div>
            The reason you cannot UNDO the receiving is because the quantity of following tag(s) changed:
            <ul>{tags.map((tag: any) => (<li key={tag.asset_id}>#{tag.asset_id}</li>))}</ul>
            <ul>
                <li>UNDO is not possible ever again via Senitron due to ERP rules, and Further modifications to PO must be managed through your ERP. Contact your back office or ERP support team.</li>
            </ul>
        </div>
    );

    showErrorAllocations = (allocation_items: Array<any>, allocations: Array<any>) => (
      <div>
        <p>You cannot undo the receiving because the following tag(s) are currently allocated to a sales order:</p>
        <ul>
            {allocation_items.map((allocation: any) => (
                <li key={allocation.asset_id}>
                    Tag #{allocation.asset_id} is allocated to Sales Order #{allocation.salesorder_id}.
                </li>
            ))}
        </ul>
        <ul>
            {allocations.map((allocation: any) => (
                <li key={allocation.asset_id}>
                    Tag #{allocation.asset_id} is allocated to Sales Order #{allocation.salesorder_id}.
                </li>
            ))}
        </ul>
        <p>To proceed, please unallocate the tag(s) from the associated sales order(s) and try again.</p>
      </div>
    );

    showErrorNav = (message: string) => (
        <div>
            The reason you cannot UNDO the receiving is because your ERP did not accept our request to UNDO.
            <div className="col-xs-12">
                Error message<br />
                <div className="sn-dark-box">{message}</div>
            </div>

            <ul>
                <li>UNDO is not possible via Senitron due to ERP error response. For further support on this PO contact your back office or ERP support team.</li>
            </ul>
        </div>
    );

    render() {
        const statuses = this.props.states.map((state) => this.props[state])

        return (
            <div className={this.getClassName()} tabIndex={-1} role="dialog">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title">Undo Process</h1>
                        </div>

                        <div className="modal-body">
                            <ul>
                                {this.props.checkTagsLive && (<li> All tags are LIVE {this.getStatus(this.props.checkTagsLive)} </li>)}
                                {this.props.checkTagsQuantities && (<li>All tags quantities are the same {this.getStatus(this.props.checkTagsQuantities)}</li>)}
                                {this.props.checkTagAllocations && (<li>Tag allocations verified {this.getStatus(this.props.checkTagAllocations)}</li>)}
                                {this.props.checkNAV && (<li>NAV UNDO {this.getStatus(this.props.checkNAV)}</li>)}
                                {this.props.checkTagsKilled && (<li>Tag killed {this.getStatus(this.props.checkTagsKilled)}</li>)}
                            </ul>

                            {statuses.some(s => s === "failed") && (
                                <div>
                                    <div className="text-warning">
                                        Sorry one or more check validation failed, thus we could not UNDO. Please contact your office or support if you have further questions.
                                    </div>
                                    <br />
                                    <div style={{ background: 'yellow' }}>
                                        {this.showError(this.props.error)}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" css={{ padding: "15px" }}>
                            {(statuses.every(s => s === "success") || statuses.some(s => s === "failed")) && (
                                <button onClick={this.handleContinue} className="man-btn man-green-btn">Continue</button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (_state: any) => ({});

const mapDispatchToProps = (_dispatch: any) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UndoProcessDialog);
